/**
 * THIS HAS A COPY ON ROC.ADMIN. DO NOT MAKE CHANGES HERE WITHOUT ALSO MAKING THEM IN THE OTHER FILE.
 */

import { Prices, PricingLine, PricingResult } from 'shared-client/types/commerce/pricing-result';

/**
 * Splits a quantity range of format `"min:max"` into a two dimensional array in the form of `[min: number, max: number]`.
 * It is illegal for quantity ranges to be fractional.
 * @param quantityRange A quantity range string in the form of `"min:max"`
 */
export function ParseQuantityRange(quantityRange: string): [number, number] {
	const s = quantityRange.split(':');
	return [parseInt(s[0]), s[1] === 'Infinity' ? Infinity : parseInt(s[1])];
}

/**
 * Retrieves a price detail of type T from a quantity range index for the specified quantity.
 * For example an index with the values `{[1:49]:"$4.99", [50:Infinity]:"$3.99"}` would return
 * "$4.99" for the quantity 1 or 25 but "3.99" for quantities above 49.
 * @param prices
 * @param quantity
 */
export function GetPriceForQuantity<T>(prices: Prices<T>, quantity: number): T {
	if (quantity == null || quantity <= 0) {
		throw new Error('Erroneous quantity passed to GetPriceForQuantity');
	}

	for (const quantityRange in prices) {
		const [min, max] = ParseQuantityRange(quantityRange);
		if (quantity >= min && quantity <= max) {
			return prices[quantityRange];
		}
	}

	throw new Error('Price not found. Is the prices object well formed?');
}

/** Retrieves a pricing line for a given pricing result by the specified pricing line ID */
export function GetPricingLine<T>(pricingResult: PricingResult<T>, productId: string, pricingLineId?: string | null) {
	const line = pricingResult.products[productId];
	if (!line) {
		return undefined;
	}

	if (pricingLineId == null) {
		for (const id in line.pricingLines) {
			const pricingLine = line.pricingLines[id];
			if (pricingLine && pricingLine.isDefault) {
				return pricingLine;
			}
		}

		for (const id in line.pricingLines) {
			const pricingLine = line.pricingLines[id];
			if (pricingLine) {
				return pricingLine;
			}
		}

		return undefined;
	}

	return line.pricingLines[pricingLineId];
}

//#region TapeCase
/**
 * Retrieves a price detail of type T from a quantity range index for the minimum quantity available.
 * For example an index with the values `{[5:24]:"$4.99", [25:Infinity]:"$3.99"}` would return "$4.99"
 * because it will use 5 as the requested quantity.
 * @param prices
 */
export function GetPriceForMinimumAvailableQuantity<T>(prices: Prices<T>): T {
	if (Object.keys(prices).length > 0) {
		const minQtyAvailable = Object.keys(prices)
			.map((qtyRange) => {
				const [min] = ParseQuantityRange(qtyRange);
				return {
					startingQty: min,
					range: qtyRange,
				};
			})
			.sort((qtyRangeInfo) => qtyRangeInfo.startingQty)[0];

		return prices[minQtyAvailable.range];
	}

	throw new Error('Price not found. Is the prices object well formed?');
}

/**
 * Retrieves a price detail of type T from a quantity range index for the specified quantity.
 * For example an index with the values `{[1:49]:"$4.99", [50:Infinity]:"$3.99"}` would return
 * "$4.99" for the quantity 1 or 25 but "3.99" for quantities above 49.
 * @param prices
 * @param quantity
 */
export function GetMinimumOrderQuantity<T>(price: PricingLine<T>): number | null {
	let moq: number | null = null;
	for (const quantityRange in price.prices) {
		const [min] = ParseQuantityRange(quantityRange);
		if (moq == null || min < moq) {
			moq = min;
		}
	}
	return moq;
}

/**
 * Get the minimum order quantity for the specified product and pricing line. Returns at least 1.
 */
export function GetMinimumOrderQuantityForPricingLine<T>(
	productPricing?: PricingResult<T>,
	productId?: string,
	selectedPricingLineId?: string,
) {
	if (!productId || !productPricing || !selectedPricingLineId) {
		return 1;
	}
	const selectedPricingLine = GetPricingLine(productPricing, productId, selectedPricingLineId);
	if (!selectedPricingLine) {
		return 1;
	}

	const minimumOrderQuantity = GetMinimumOrderQuantity(selectedPricingLine) ?? 0;
	// Return at least 1 as the minimum
	return Math.max(1, minimumOrderQuantity);
}

//#endregion
